import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../components/ui/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IGNORE_REASON_LABEL, IgnoreReason } from '@masamune/shared';
import { IGNORE_REASONS, type IgnoreReasonKey } from './adminApi';

/**
 * Popover "Togli dall'elenco" (era "Cestina"): motivo (obbligatorio), nota
 * (facoltativa) e la riga che dice cosa succede davvero.
 *
 * Togliere dall'elenco toglie il documento da Masamune OS e basta: su
 * FattureInCloud non si scrive niente e niente viene cancellato dal database.
 * Va detto qui, in una riga, perché accanto a una fattura fa pensare a un
 * annullamento fiscale, che è tutta un'altra cosa (serve una nota di credito,
 * la fa il commercialista).
 *
 * Default "Emessa per errore": è il caso più comune. Il motivo resta scritto sul
 * documento e si rilegge fra le fatture tolte dall'elenco.
 */

/**
 * I motivi con parole intere (glossario § 5): "SdI" solo fra parentesi. La
 * lista vive in @masamune/shared (IGNORE_REASON_LABEL), la stessa per tutti:
 * questo nome resta per chi lo importa già (Spese da pagare).
 */
export const MOTIVO_TOLTA_LABEL: Record<IgnoreReasonKey, string> = IGNORE_REASON_LABEL;

export function TrashPopover({ title, pending, onConfirm, onClose, cosa = 'fattura' }: {
  /** Cosa si sta togliendo: "2026/57" oppure "5 fatture". */
  title: string;
  pending?: boolean;
  /** Per la frase in fondo: "fattura" (femminile) o "documenti". */
  cosa?: 'fattura' | 'fatture';
  onConfirm: (v: { reason: IgnoreReasonKey; note: string | null }) => void;
  onClose: () => void;
}) {
  const mobile = useIsMobile();
  const [reason, setReason] = useState<IgnoreReasonKey>(IgnoreReason.ERRORE);
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    if (pending) return;
    onConfirm({ reason, note: note.trim() || null });
  };

  const plurale = cosa === 'fatture';
  const cosaTogli = plurale ? "Togli le fatture dall'elenco" : "Togli la fattura dall'elenco";
  // Sul telefono è un foglio dal basso: fuori dalla riga, o resta sotto la barra in basso (vedi SettlePopover).
  const corpo = (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      {/* Più largo dei 320px di .adm-pop: "Rifiutata dall'Agenzia delle Entrate
          (SdI)" non deve arrivare tagliato a metà nel select. */}
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label={cosaTogli}
        style={{ width: 'min(400px, calc(100vw - 32px))' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}>
        <div className="adm-pop-hd">
          <span className="t-micro">{cosaTogli}</span>
          <span className="mono font-semibold truncate">{title}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">{plurale ? 'Perché le togli' : 'Perché la togli'}</label>
          <select className="inp inp-sm" value={reason} onChange={(e) => setReason(e.target.value as IgnoreReasonKey)} autoFocus>
            {IGNORE_REASONS.map((r) => <option key={r} value={r}>{IGNORE_REASON_LABEL[r]}</option>)}
          </select>
          <label className="field-label">Nota</label>
          <input className="inp inp-sm" placeholder="Facoltativa" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          {plurale ? 'Spariscono' : 'Sparisce'} da questo elenco e dai totali, e {plurale ? 'si possono rimettere' : 'si può rimettere'} quando serve.
          Su FattureInCloud {plurale ? 'restano' : 'resta'}: per annullare davvero una fattura serve una nota di credito.
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm" disabled={pending} onClick={submit}>
            <Icon name="trash" size={10} /> {cosaTogli}
          </button>
        </div>
      </div>
    </>
  );
  return mobile ? createPortal(corpo, document.body) : corpo;
}
